import React, { useState, useEffect } from "react";
import { v4 as uuidV4 } from "uuid";

// Helpers
import OutsideAlerter from "../../helpers/OutsideAlerter";
import { escapeRegExp } from "lodash";

import { SquareXButton } from "../Buttons/Buttons";

const getTagSuggestions = ({
  searchString,
  suggestionList,
  tags,
  clickTag,
}) => {
  const escapedSearchString = escapeRegExp(searchString);
  const regex = new RegExp(escapedSearchString, "i");

  const notUsedTags = suggestionList.filter((item) => !tags.includes(item));

  // Filter the notUsedTags array to only include tags that match the search string
  const matchingTags = notUsedTags.filter((tag) => regex.test(tag));

  // Sort the matching tags based on the position of the search string in the original tag
  matchingTags.sort((a, b) => {
    const aIndex = a.toLowerCase().indexOf(searchString.toLowerCase());
    const bIndex = b.toLowerCase().indexOf(searchString.toLowerCase());
    return aIndex - bIndex;
  });

  const autoCompleteSuggestions = matchingTags.map((tag, index) => {
    const matchIndex = tag.toLowerCase().indexOf(searchString.toLowerCase());
    const before = (
      <p className="whitespace-pre-wrap">{tag.slice(0, matchIndex)}</p>
    );
    const after = (
      <p className="whitespace-pre-wrap">
        {tag.slice(matchIndex + searchString.length)}
      </p>
    );
    const highlighted = (
      <mark className="bg-amber-200 rounded-md text-base font-semibold whitespace-pre-wrap">
        {tag.slice(matchIndex, matchIndex + searchString.length)}
      </mark>
    );

    return (
      <div
        key={`${uuidV4()}-${index}`}
        className="py-1 first-of-type:pt-0 last-of-type:pb-0"
      >
        <div
          className="flex flex-row items-start justify-start w-full p-2 rounded-md bg-white
                     cursor-pointer select-none border border-1 border-neutral-300
                   hover:bg-lime-100
                    "
          key={tag}
          onClick={() => clickTag(tag)}
        >
          {before}
          {highlighted}
          {after}
        </div>
      </div>
    );
  });

  return autoCompleteSuggestions;
};

const SelectedTagItem = ({ tag, onRemove }) => {
  return (
    <div className="pr-2 pb-2">
      <div className="flex flex-row items-center justify-start p-2 rounded-md bg-neutral-200">
        <div className="text-base font-medium text-neutral-900">{tag}</div>
        <div className="px-1" />
        <SquareXButton
          iconSize="h-4 w-4"
          actionFunction={() => {
            onRemove(tag);
          }}
        />
      </div>
    </div>
  );
};

const TagInputField = (props) => {
  const {
    suggestionList,
    searchString,
    setSearchString,
    setAutoCompleteSuggestions,
    tags,
    setTags,
    openSuggestions,
    setFieldFocused,
  } = props;

  // Load autoCompleteSuggestions on component load
  useEffect(() => {
    setAutoCompleteSuggestions(
      getTagSuggestions({
        searchString: "",
        suggestionList: suggestionList,
        tags: tags,
        clickTag: handleSuggestionClick,
      })
    );
  }, [suggestionList]);

  //  Handle change when user types
  function handleInputChange(event) {
    const value = event.target.value;

    const loweredValue = value.toLowerCase();

    setSearchString(loweredValue);

    setAutoCompleteSuggestions(
      getTagSuggestions({
        searchString: loweredValue,
        suggestionList: suggestionList,
        tags: tags,
        clickTag: handleSuggestionClick,
      })
    );
  }

  // Handle keys pressed inside of text input field
  function handleKeyDown(event) {
    // Pressing Enter adds tag
    if (event.key === "Enter" && searchString.trim() !== "") {
      if (!tags.includes(searchString.trim())) {
        setTags([...tags, searchString.trim()]);
        setSearchString("");
      }
      // Pressing Delete removes the last tag
    } else if (event.key === "Backspace" && searchString === "") {
      handleRemoveWord(tags[tags.length - 1]);
    }
  }

  // Handle user clicking on a suggestion tag
  function handleSuggestionClick(tag) {
    setTags((prevSelectedWords) => {
      if (prevSelectedWords.includes(tag)) {
        setSearchString("");
        return prevSelectedWords;
      } else {
        setSearchString("");
        return [...prevSelectedWords, tag];
      }
    });
  }

  // Handle user clicking on X of a suggestion tag. Remove this tag.
  function handleRemoveWord(tag) {
    setTags((prevSelectedWords) => prevSelectedWords.filter((w) => w !== tag));
  }

  return (
    <input
      placeholder="Press ENTER to add a new tag"
      className="flex w-full items-center p-2 border border-neutral-300 rounded-lg
        focus:outline-none focus:ring-inset focus:ring-neutral-300"
      type="text"
      value={searchString}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        openSuggestions();
        setFieldFocused(true);
      }}
      onBlur={() => {
        setFieldFocused(false);
      }}
      maxLength={75}
    />
  );
};

const TagAutoSuggest = (props) => {
  const { tags, setTags, suggestionList } = props;

  const [searchString, setSearchString] = useState("");
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState([]);
  const [suggestionsModalOpen, setSuggestionsModalOpen] = useState(false);
  const [fieldFocused, setFieldFocused] = useState(false);

  const [modalFocused, setModalFocused] = useState(false);

  // Handle user clicking on X of a suggestion tag. Remove this tag.
  function handleRemoveWord(tag) {
    setTags((prevSelectedWords) => prevSelectedWords.filter((w) => w !== tag));
  }

  // Handle clicking off the suggestion list
  const closeSuggestionModal = () => {
    if (!fieldFocused && !modalFocused) {
      setSuggestionsModalOpen(false);
    }
  };

  const openSuggestions = () => {
    if (
      searchString.length > 2 &&
      autoCompleteSuggestions.length > 0 &&
      (fieldFocused || modalFocused)
    ) {
      setSuggestionsModalOpen(true);
    } else {
      setSuggestionsModalOpen(false);
    }
  };

  useEffect(() => {
    openSuggestions();
  }, [searchString, autoCompleteSuggestions, fieldFocused, modalFocused]);

  return (
    <div className="flex flex-col items-center justify-center Xbg-red-500">
      {/* <h3>{JSON.stringify(tags)}</h3> */}
      {/* <h3>{JSON.stringify(suggestionsModalOpen)}</h3> */}
      {/* <h3>modalFocused: {JSON.stringify(modalFocused)}</h3>
      <h3>fieldFocused: {JSON.stringify(fieldFocused)}</h3> */}

      <div
        className={`flex flex-row items-start justify-start w-full flex-wrap ${
          tags.length > 0 && "py-2"
        }`}
      >
        {tags.map((tag, matchIndex) => (
          <SelectedTagItem
            key={`${uuidV4()}-${matchIndex}`}
            tag={tag}
            onRemove={handleRemoveWord}
          />
        ))}
      </div>

      {suggestionsModalOpen && (
        <OutsideAlerter onOutsideClick={closeSuggestionModal}>
          <div className="relative w-full">
            <div
              className="absolute bottom-4 z-[950] flex flex-col items-start justify-start p-4 max-h-96 
              rounded-xl bg-neutral-100 shadow-xl outline outline-1
              overflow-scroll overscroll-contain
              "
              onMouseEnter={() => {
                setModalFocused(true);
              }}
              onMouseLeave={() => {
                setModalFocused(false);
              }}
            >
              {autoCompleteSuggestions}
            </div>
          </div>
        </OutsideAlerter>
      )}

      <TagInputField
        suggestionList={suggestionList}
        searchString={searchString}
        setSearchString={setSearchString}
        setAutoCompleteSuggestions={setAutoCompleteSuggestions}
        tags={tags}
        setTags={setTags}
        suggestionsModalOpen={suggestionsModalOpen}
        openSuggestions={openSuggestions}
        setFieldFocused={setFieldFocused}
      />
    </div>
  );
};

export default TagAutoSuggest;
